/**
 * "I'm travelling — keep it", remembered on the browser that said it.
 *
 * The rules — what a pairing is, when a stored answer covers the situation on
 * screen, how many to keep — are in `@calorio/core`'s `zones`, because the
 * phone shows the same warning and has to stop showing it at the same moment.
 * What is here is only where the answer rests: `localStorage`, synchronously,
 * because the banner is decided during a render.
 *
 * `localStorage` rather than `sessionStorage`, and rather than the profile. Not
 * the profile because devices are exactly what disagree — a phone that follows
 * the traveller, a desktop left on UTC — and a server-side "don't ask" would
 * silence the device that is genuinely wrong along with the one that is
 * deliberately different. Not the session because an answer that evaporates
 * when the tab closes is the banner-every-visit this exists to end.
 *
 * Every access is wrapped. Storage throws rather than returning null in Safari
 * with site data blocked, and a private window is not a reason for Today to be
 * a white page. A failed write costs this device the answer on the next load,
 * which is the behaviour that existed before this file.
 */

import { rememberZone, zoneKept as coveredBy } from "@calorio/core";

const KEY = "calorio:zone-kept";

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** Has this device already been told to keep the profile zone in this place? */
export function zoneKept(profileZone: string, deviceZone: string): boolean {
  return coveredBy(read(), profileZone, deviceZone);
}

/** Remember that the user chose to keep the profile zone here. */
export function keepZone(profileZone: string, deviceZone: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(rememberZone(read(), profileZone, deviceZone)));
  } catch {
    // The banner still goes away for this visit; it just comes back on the next
    // load, which is where this started.
  }
}
